import { ProjectFormData, CHSTeamMember, Participant, CHS_SUBDOMAINS } from '../types/project-form';

export const MIN_TEAM_MEMBERS = 6;
export const MAX_TEAM_MEMBERS = 10;
export const PHONE_LENGTH_MESSAGE = 'Invalid phone number; the phone number should not be more than ten digits or less than ten digits.';
export const PHONE_DUPLICATE_MESSAGE = 'Invalid phone number; This phone number is already in use by someone else.';

/**
 * Validates form data and returns validation errors
 */
export const validateFormData = (formData: ProjectFormData): Partial<Record<keyof ProjectFormData, string>> => {
  const newErrors: Partial<Record<keyof ProjectFormData, string>> = {};

  // Basic field validations
  if (!formData.projectTitle?.trim()) {
    newErrors.projectTitle = 'Project title is required';
  }

  if (!formData.responsiblePerson?.trim()) {
    newErrors.responsiblePerson = 'Responsible person is required';
  }

  if (!formData.programmeArea) {
    newErrors.programmeArea = 'Programme area is required';
  }

  if (!formData.domain) {
    newErrors.domain = 'Domain is required';
  }

  // Only require subdomain if the selected domain has subdomains available
  if (formData.domain && CHS_SUBDOMAINS[formData.domain] && CHS_SUBDOMAINS[formData.domain].length > 0) {
    if (!formData.subDomain) {
      newErrors.subDomain = 'Subdomain is required for this domain';
    }
  }

  if (!formData.problemStatement?.trim()) {
    newErrors.problemStatement = 'Problem statement is required';
  }

  // Date validations
  if (!formData.firstCycleDate) {
    newErrors.firstCycleDate = 'First cycle date is required';
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstCycleDate = new Date(formData.firstCycleDate);

    if (firstCycleDate < today) {
      newErrors.firstCycleDate = 'First cycle date cannot be in the past';
    }
  }

  if (!formData.projectEndDate) {
    newErrors.projectEndDate = 'Project end date is required';
  } else if (formData.firstCycleDate) {
    const firstCycleDate = new Date(formData.firstCycleDate);
    const projectEndDate = new Date(formData.projectEndDate);

    if (projectEndDate <= firstCycleDate) {
      newErrors.projectEndDate = 'Project end date must be after start date';
    }

    const sixMonthsFromStart = new Date(firstCycleDate);
    sixMonthsFromStart.setMonth(sixMonthsFromStart.getMonth() + 6);

    if (projectEndDate > sixMonthsFromStart) {
      newErrors.projectEndDate = 'Project end date cannot be more than 6 months from start date';
    }
  }

  // Numerator and Denominator validation - Required for all projects
  if (!formData.indicatorNumerator?.trim()) {
    newErrors.indicatorNumerator = 'Numerator description is required';
  }

  if (!formData.indicatorDenominator?.trim()) {
    newErrors.indicatorDenominator = 'Denominator description is required';
  }

  // Note: numeratorNumber and denominatorNumber fields have been removed
  // We now only use indicatorNumerator and indicatorDenominator (text descriptions)

  // QI Project Team size validation (CHS projects)
  if (formData.programmeArea === 'Community Health Services') {
    const teamSize = (formData.chsTeamMembers || []).length;
    if (teamSize < MIN_TEAM_MEMBERS) {
      newErrors.chsTeamMembers = `Please add at least ${MIN_TEAM_MEMBERS} team members (current: ${teamSize})`;
    } else if (teamSize > MAX_TEAM_MEMBERS) {
      newErrors.chsTeamMembers = `Project team cannot exceed ${MAX_TEAM_MEMBERS} members (current: ${teamSize})`;
    }
  }

  // Potential Gaps validation
  const potentialGaps = formData.potentialGaps || [];
  if (formData.programmeArea === 'Community Health Services' && potentialGaps.length > 0) {
    const emptyGaps = potentialGaps.filter(gap => !gap || (typeof gap === 'string' && gap.trim() === ''));
    if (emptyGaps.length > 0) {
      newErrors.potentialGaps = `Please fill in all potential gaps or remove empty ones (${emptyGaps.length} empty)`;
    }
  }

  const hasErrors = Object.keys(newErrors).length > 0;

  return newErrors;
};

/**
 * Validates team members and returns validation errors
 */
export const validateTeamMembers = (chsTeamMembers: CHSTeamMember[]): Record<number, Record<string, string>> => {
  const teamMemberErrors: Record<number, Record<string, string>> = {};

  // Track phone numbers already claimed by an earlier member to flag duplicates
  const seenPhoneNumbers = new Set<string>();

  chsTeamMembers.forEach((member, index) => {
    const memberErrors: Record<string, string> = {};

    if (!member.fullName?.trim()) {
      memberErrors.fullName = 'Full name is required';
    }

    const phone = member.phoneNumber?.trim() || '';
    if (!phone) {
      memberErrors.phoneNumber = 'Phone number is required';
    } else {
      const numericValue = phone.replace(/[^0-9]/g, '');
      if (phone !== numericValue || numericValue.length !== 10) {
        memberErrors.phoneNumber = PHONE_LENGTH_MESSAGE;
      } else if (seenPhoneNumbers.has(numericValue)) {
        memberErrors.phoneNumber = PHONE_DUPLICATE_MESSAGE;
      } else {
        seenPhoneNumbers.add(numericValue);
      }
    }

    if (!member.role?.trim()) {
      memberErrors.role = 'Role is required';
    } else if (member.role === 'Other' && !member.customRole?.trim()) {
      memberErrors.customRole = 'Please specify the custom role';
    }

    if (Object.keys(memberErrors).length > 0) {
      teamMemberErrors[index] = memberErrors;
    }
  });

  return teamMemberErrors;
};

/**
 * Validates participants and returns validation errors
 */
export const validateParticipants = (participants: Participant[]): Record<number, Record<string, string>> => {
  const participantErrors: Record<number, Record<string, string>> = {};

  participants.forEach((participant, index) => {
    const errors: Record<string, string> = {};

    if (!participant.fullName?.trim()) {
      errors.fullName = 'Full name is required';
    }

    if (participant.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participant.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (Object.keys(errors).length > 0) {
      participantErrors[index] = errors;
    }
  });

  return participantErrors;
};
